import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./themeupdate.css";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { fetchThemeData } from "../../themeSlice";
import { useNavigate } from "react-router-dom";

const MAX_QUESTION_LEN = 350;
const MAX_OPTION_LEN = 100;
const MAX_INFO_LEN = 350;

// Parses CSV text into an array of row objects keyed by header name.
// Handles quoted fields (including embedded commas and escaped "" quotes),
// mirroring what a spreadsheet export would produce.
function parseCsvText(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;

  const pushField = () => {
    row.push(field);
    field = "";
  };
  const pushRow = () => {
    pushField();
    rows.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      pushField();
    } else if (char === "\r") {
      // skip, \n handles the row break
    } else if (char === "\n") {
      pushRow();
    } else {
      field += char;
    }
  }
  if (field !== "" || row.length > 0) {
    pushRow();
  }

  const nonEmptyRows = rows.filter((r) => !(r.length === 1 && r[0].trim() === ""));
  if (nonEmptyRows.length === 0) return [];

  const headers = nonEmptyRows[0].map((h) => h.trim());
  return nonEmptyRows.slice(1).map((r) => {
    const obj = {};
    headers.forEach((h, idx) => {
      obj[h] = (r[idx] ?? "").trim();
    });
    return obj;
  });
}

// Mirrors the server-side rules in controllers/mythFactColumns.js so a bad
// file is rejected instantly, before any network round-trip.
function validateCsvRows(rows) {
  if (rows.length === 0) {
    return "CSV file is empty";
  }

  for (let i = 0; i < rows.length; i++) {
    const q = rows[i];
    const rowLabel = `Row ${i + 2}`;

    const question_name = String(q.question_name || "").trim();
    const option_one = String(q.option_one || "").trim();
    const option_two = String(q.option_two || "").trim();
    const information = String(q.information || "").trim();
    const answer_id = String(q.answer_id || "").trim();
    const correct_answer = String(q.correct_answer || "").trim();

    if (!question_name) return `${rowLabel}: question is required`;
    if (question_name.length > MAX_QUESTION_LEN) {
      return `${rowLabel}: question must not exceed ${MAX_QUESTION_LEN} characters`;
    }
    if (!option_one) return `${rowLabel}: option_one is required`;
    if (option_one.length > MAX_OPTION_LEN) {
      return `${rowLabel}: option_one must not exceed ${MAX_OPTION_LEN} characters`;
    }
    if (!option_two) return `${rowLabel}: option_two is required`;
    if (option_two.length > MAX_OPTION_LEN) {
      return `${rowLabel}: option_two must not exceed ${MAX_OPTION_LEN} characters`;
    }
    if (!information) return `${rowLabel}: information is required`;
    if (information.length > MAX_INFO_LEN) {
      return `${rowLabel}: information must not exceed ${MAX_INFO_LEN} characters`;
    }
    if (answer_id !== "1" && answer_id !== "2") {
      return `${rowLabel}: answer_id must be 1 or 2. Found "${q.answer_id}".`;
    }
    const expectedCorrect = answer_id === "1" ? option_one : option_two;
    if (correct_answer && correct_answer !== expectedCorrect) {
      return `${rowLabel}: correct_answer "${correct_answer}" does not match option ${answer_id} ("${expectedCorrect}")`;
    }
  }

  return null;
}

const emptyQuestion = () => ({
  id: null,
  question_name: "",
  option_one: "",
  option_two: "",
  information: "",
  answer_id: "",
});

const ThemeUpdate = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data: themeConfig } = useSelector((state) => state.theme);

  const [questions, setQuestions] = useState([]);
  const [questionsLoaded, setQuestionsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [formData, setFormData] = useState(emptyQuestion());
  const [csvFile, setCsvFile] = useState(null);
  const [csvFileName, setCsvFileName] = useState("");
  const csvInputRef = useRef(null);
  // Guards the "not enough questions" popup to a single check on landing —
  // not re-fired on every add/delete within the same visit.
  const shortfallPopupShownRef = useRef(false);

  useEffect(() => {
    if (currentTheme && adminToken) loadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTheme, adminToken]);

  // total_question lives on the theme config, not the questions list — fetch
  // it here too (rather than assuming a prior page already loaded it) so the
  // shortfall check below is accurate on a direct hit of this page.
  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (shortfallPopupShownRef.current) return;
    if (!questionsLoaded || !themeConfig) return;
    const totalWanted = Number(themeConfig.total_question) || 0;
    if (totalWanted > 0 && questions.length < totalWanted) {
      shortfallPopupShownRef.current = true;
      Swal.fire(
        "Not Enough Questions",
        `"Number of questions to display" is set to ${totalWanted}, but only ${questions.length} question(s) exist for this theme. Please add more question.`,
        "warning"
      );
    }
  }, [questionsLoaded, questions, themeConfig]);

  const loadQuestions = async () => {
    if (!currentTheme || !adminToken) return;
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/getMythFactQuestions`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setQuestions(data.questions || []);
      } else {
        throw new Error(data.message || "Failed to load questions");
      }
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to load questions", "error");
    } finally {
      setLoading(false);
      setQuestionsLoaded(true);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectAnswer = (optionNumber) => {
    setFormData((prev) => ({ ...prev, answer_id: String(optionNumber) }));
  };

  const validateForm = () => {
    if (!formData.question_name.trim()) {
      Swal.fire("Validation", "Question is required", "warning");
      return false;
    }
    if (formData.question_name.length > MAX_QUESTION_LEN) {
      Swal.fire("Validation", `Question must not exceed ${MAX_QUESTION_LEN} characters`, "warning");
      return false;
    }
    if (!formData.option_one.trim()) {
      Swal.fire("Validation", "Option one is required", "warning");
      return false;
    }
    if (formData.option_one.length > MAX_OPTION_LEN) {
      Swal.fire("Validation", `Option one must not exceed ${MAX_OPTION_LEN} characters`, "warning");
      return false;
    }
    if (!formData.option_two.trim()) {
      Swal.fire("Validation", "Option two is required", "warning");
      return false;
    }
    if (formData.option_two.length > MAX_OPTION_LEN) {
      Swal.fire("Validation", `Option two must not exceed ${MAX_OPTION_LEN} characters`, "warning");
      return false;
    }
    if (!formData.information.trim()) {
      Swal.fire("Validation", "Information is required", "warning");
      return false;
    }
    if (formData.information.length > MAX_INFO_LEN) {
      Swal.fire("Validation", `Information must not exceed ${MAX_INFO_LEN} characters`, "warning");
      return false;
    }
    if (formData.answer_id !== "1" && formData.answer_id !== "2") {
      Swal.fire("Validation", "Select the correct answer by clicking one of the options", "warning");
      return false;
    }
    return true;
  };

  const handleSaveQuestion = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/saveMythFactQuestion`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
          action: editingQuestion ? "edit" : "add",
          edit_id: editingQuestion?.id,
          question_name: formData.question_name.trim(),
          option_one: formData.option_one.trim(),
          option_two: formData.option_two.trim(),
          information: formData.information.trim(),
          answer_id: formData.answer_id,
        }),
      });
      const data = await res.json();
      if (data.success) {
        Swal.fire({
          icon: "success",
          title: editingQuestion ? "Updated" : "Added",
          timer: 1200,
          showConfirmButton: false,
        });
        setShowAddForm(false);
        setEditingQuestion(null);
        setFormData(emptyQuestion());
        await loadQuestions();
      } else {
        throw new Error(data.message || "Failed to save question");
      }
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to save question", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    const result = await Swal.fire({
      title: "Are you sure you want to delete this item?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });
    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/deleteMythFactQuestion`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
          id: questionId,
        }),
      });
      const data = await res.json();
      if (data.success) {
        await loadQuestions();
      } else {
        throw new Error(data.message || "Failed to delete question");
      }
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to delete question", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setFormData({
      id: question.id,
      question_name: question.question_name || "",
      option_one: question.option_one || "",
      option_two: question.option_two || "",
      information: question.information || "",
      answer_id: String(question.answer_id || ""),
    });
    setShowAddForm(true);
  };

  const resetCsvSelection = () => {
    setCsvFile(null);
    setCsvFileName("");
    if (csvInputRef.current) csvInputRef.current.value = "";
  };

  const handleCsvFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "csv") {
      Swal.fire("Error", "Only CSV files allowed", "error").then(() => {
        resetCsvSelection();
      });
      return;
    }

    setCsvFile(file);
    setCsvFileName(file.name);
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      Swal.fire("Info", "Please select a CSV file first", "info");
      return;
    }

    let csvText;
    try {
      csvText = await csvFile.text();
    } catch (e) {
      Swal.fire("Error", "Failed to read the selected file", "error").then(() => resetCsvSelection());
      return;
    }

    const parsedRows = parseCsvText(csvText);
    const validationError = validateCsvRows(parsedRows);
    if (validationError) {
      Swal.fire("Please check excel", validationError, "error").then(() => resetCsvSelection());
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("organizationId", organizationId || "admin");
      formData.append("sessionId", sessionId || "admin");
      formData.append("currentTheme", currentTheme);
      formData.append("csvFile", csvFile);

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/importMythFactQuestionsCSV`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: formData,
      });

      const data = await res.json();
      if (data.success) {
        Swal.fire({
          icon: "success",
          title: "Import Successful!",
          html: `<p>${data.message || "CSV imported successfully"}</p><p>Total questions imported: <strong>${data.count || 0}</strong></p>`,
        });
        setShowCsvModal(false);
        resetCsvSelection();
        await loadQuestions();
      } else {
        throw new Error(data.message || "Failed to import CSV");
      }
    } catch (error) {
      Swal.fire("Please check excel", error.message || "Failed to import CSV", "error").then(() => resetCsvSelection());
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = async () => {
    if (questions.length === 0) {
      Swal.fire("Info", "No questions to export", "info");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/exportMythFactQuestionsCSV`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        }),
      });
      if (!res.ok) throw new Error("Failed to export CSV");

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "myth-fact-questions.csv";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to export CSV", "error");
    } finally {
      setLoading(false);
    }
  };

  const downloadSampleCsv = () => {
    const headers = ["question_name", "option_one", "option_two", "information", "correct_answer", "answer_id"];
    const sampleData = [
      [
        "The Great Wall of China is visible from the Moon with the naked eye.",
        "Myth",
        "Fact",
        "A commonly repeated claim that has been debunked by astronauts.",
        "Myth",
        "1",
      ],
      [
        "Honey never spoils if stored properly.",
        "Myth",
        "Fact",
        "Archaeologists have found edible honey in ancient Egyptian tombs.",
        "Fact",
        "2",
      ],
    ];
    const csvContent = [headers.join(","), ...sampleData.map((row) => row.map((cell) => `"${cell}"`).join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "myth-fact-sample.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="themeupdate-page">
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>

      <div className="themeupdate-panel-wide">
        <div className="themeupdate-card">
          <div className="themeupdate-card-header">Myth-Fact</div>

          <div className="themeupdate-actions-row">
            <button
              type="button"
              className="themeupdate-btn themeupdate-btn-success"
              onClick={() => {
                setShowAddForm(true);
                setEditingQuestion(null);
                setFormData(emptyQuestion());
              }}
              disabled={loading}
            >
              <i className="fa-solid fa-plus"></i> Add Question
            </button>
            <button
              type="button"
              className="themeupdate-btn themeupdate-btn-primary"
              onClick={() => setShowCsvModal(true)}
              disabled={loading}
            >
              <i className="fa-solid fa-upload"></i> Upload CSV
            </button>
            <button
              type="button"
              className="themeupdate-btn themeupdate-btn-primary"
              onClick={handleExportCsv}
              disabled={loading || questions.length === 0}
            >
              <i className="fa-solid fa-download"></i> Export CSV
            </button>
            <button type="button" className="themeupdate-btn themeupdate-btn-outline" onClick={downloadSampleCsv}>
              <i className="fa-solid fa-file-csv"></i> Sample CSV
            </button>
          </div>

          {/* CSV Upload Modal */}
          {showCsvModal && (
            <div className="themeupdate-modal-overlay">
              <div className="themeupdate-modal themeupdate-modal-csv" onClick={(e) => e.stopPropagation()}>
                <div className="themeupdate-modal-header">
                  <h3 className="themeupdate-modal-title">Upload CSV File</h3>
                  <button
                    type="button"
                    className="themeupdate-modal-close"
                    onClick={() => {
                      setShowCsvModal(false);
                      resetCsvSelection();
                    }}
                    disabled={loading}
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                </div>

                <div className="themeupdate-modal-body">
                  <div className="themeupdate-csv-upload-container">
                    <div className="themeupdate-csv-warning">
                      <i className="fa-solid fa-exclamation-triangle"></i>
                      <div>
                        <strong>Warning:</strong> This will DELETE all existing questions and replace them with the questions from the CSV file.
                        <br />
                        Current questions: <strong>{questions.length}</strong>
                      </div>
                    </div>

                    <div className="themeupdate-csv-upload-area">
                      <input
                        ref={csvInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleCsvFileSelect}
                        className="themeupdate-file-input"
                        id="csv-upload-input-modal"
                      />
                      <label htmlFor="csv-upload-input-modal" className="themeupdate-file-label-large">
                        <i className="fa-solid fa-file-csv"></i>
                        <span>{csvFileName || "Choose CSV File"}</span>
                      </label>
                    </div>

                    <div className="themeupdate-csv-instructions">
                      <h4>CSV File Requirements:</h4>
                      <ul>
                        <li>
                          CSV must have headers: <code>question_name, option_one, option_two, information, correct_answer, answer_id</code>
                        </li>
                        <li>Questions will be overwritten (existing questions for this theme are replaced).</li>
                        <li>question_name max {MAX_QUESTION_LEN} characters.</li>
                        <li>option_one / option_two max {MAX_OPTION_LEN} characters each.</li>
                        <li>information max {MAX_INFO_LEN} characters.</li>
                        <li>answer_id must be exactly 1 or 2, and must match the correct_answer column.</li>
                        <li>Whitespace will be trimmed automatically.</li>
                      </ul>
                    </div>
                  </div>

                  <div className="themeupdate-form-actions">
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-primary"
                      onClick={handleCsvUpload}
                      disabled={!csvFile || loading}
                    >
                      {loading ? "Uploading..." : "Upload & Replace All Questions"}
                    </button>
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-outline"
                      onClick={() => {
                        setShowCsvModal(false);
                        resetCsvSelection();
                      }}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Add/Edit Question Modal */}
          {showAddForm && (
            <div className="themeupdate-modal-overlay">
              <div className="themeupdate-modal" onClick={(e) => e.stopPropagation()}>
                <div className="themeupdate-modal-header">
                  <h3 className="themeupdate-modal-title">{editingQuestion ? "Edit Question" : "Add Question"}</h3>
                  <button
                    type="button"
                    className="themeupdate-modal-close"
                    onClick={() => {
                      setShowAddForm(false);
                      setEditingQuestion(null);
                      setFormData(emptyQuestion());
                    }}
                    disabled={loading}
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                </div>

                <div className="themeupdate-modal-body">
                  <div className="themeupdate-form-group themeupdate-form-group-full">
                    <label className="themeupdate-label">Question * (max {MAX_QUESTION_LEN} chars)</label>
                    <textarea
                      className="themeupdate-input themeupdate-textarea"
                      value={formData.question_name}
                      onChange={(e) => handleFormChange("question_name", e.target.value)}
                      placeholder="Question"
                      rows={3}
                      maxLength={MAX_QUESTION_LEN}
                    />
                  </div>

                  <p className="themeupdate-muted mf-select-hint">
                    <code>* Click the correct option below to mark it as the right answer</code>
                  </p>

                  <div className="mf-options-row">
                    <div
                      className={`mf-option-box ${formData.answer_id === "1" ? "selected" : ""}`}
                      onClick={() => handleSelectAnswer(1)}
                    >
                      <div className="mf-option-badge">{formData.answer_id === "1" ? <i className="fa-solid fa-check"></i> : "1"}</div>
                      <input
                        type="text"
                        className="themeupdate-input"
                        value={formData.option_one}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleFormChange("option_one", e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Option one"
                        maxLength={MAX_OPTION_LEN}
                      />
                    </div>
                    <div
                      className={`mf-option-box ${formData.answer_id === "2" ? "selected" : ""}`}
                      onClick={() => handleSelectAnswer(2)}
                    >
                      <div className="mf-option-badge">{formData.answer_id === "2" ? <i className="fa-solid fa-check"></i> : "2"}</div>
                      <input
                        type="text"
                        className="themeupdate-input"
                        value={formData.option_two}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleFormChange("option_two", e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Option two"
                        maxLength={MAX_OPTION_LEN}
                      />
                    </div>
                  </div>
                  <p className="themeupdate-muted">
                    <code>* Max {MAX_OPTION_LEN} characters per option</code>
                  </p>

                  <div className="themeupdate-form-group themeupdate-form-group-full">
                    <label className="themeupdate-label">Information * (max {MAX_INFO_LEN} chars)</label>
                    <textarea
                      className="themeupdate-input themeupdate-textarea"
                      value={formData.information}
                      onChange={(e) => handleFormChange("information", e.target.value)}
                      placeholder="Explanation shown for this question"
                      rows={4}
                      maxLength={MAX_INFO_LEN}
                    />
                  </div>

                  <div className="themeupdate-form-actions">
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-success"
                      onClick={handleSaveQuestion}
                      disabled={loading}
                    >
                      {loading ? "Saving..." : editingQuestion ? "Update" : "Add Question"}
                    </button>
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-outline"
                      onClick={() => {
                        setShowAddForm(false);
                        setEditingQuestion(null);
                        setFormData(emptyQuestion());
                      }}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Questions Table */}
          <div className="themeupdate-table-section">
            <h3 className="themeupdate-section-title">Questions List ({questions.length})</h3>

            {loading ? (
              <div className="themeupdate-loading">Loading questions...</div>
            ) : questions.length === 0 ? (
              <div className="themeupdate-empty">
                <i className="fa-solid fa-inbox"></i>
                <p>No questions added yet. Add questions using the form above or import from CSV.</p>
              </div>
            ) : (
              <div className="themeupdate-table-wrapper">
                <table className="themeupdate-table">
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>Question</th>
                      <th>Correct Answer</th>
                      <th>Information</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {questions.map((q, index) => (
                      <tr key={q.id}>
                        <td>{index + 1}</td>
                        <td className="themeupdate-td-ellipsis">{q.question_name}</td>
                        <td>{q.answer_id === "1" || q.answer_id === 1 ? q.option_one : q.option_two}</td>
                        <td className="themeupdate-td-ellipsis">{q.information}</td>
                        <td>
                          <div className="themeupdate-table-actions">
                            <button
                              type="button"
                              className="themeupdate-btn-icon themeupdate-btn-icon-edit"
                              onClick={() => handleEditQuestion(q)}
                              title="Edit"
                            >
                              <i className="fa-solid fa-edit"></i>
                            </button>
                            <button
                              type="button"
                              className="themeupdate-btn-icon themeupdate-btn-icon-delete"
                              onClick={() => handleDeleteQuestion(q.id)}
                              title="Delete"
                            >
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemeUpdate;
